import { useCallback, useRef, useState } from 'react'

import type { Timestamp } from '@bufbuild/protobuf/wkt'
import { BidSegment } from '@ebalance/proto/bfftypes/v1/bid_segment_pb'
import type {
  BidLimitCell,
  BidLimitSegmentMeta,
} from '@ebalance/proto/bffuser/v1/bid_limit_model_pb'

import type {
  BidLimitValue,
  MatrixDraft,
} from '~/pages/AdminBidLimits/models/bidLimitMatrix'
import {
  emptyMatrixDraft,
  isMatrixDirty,
  matrixDraftFromCells,
} from '~/pages/AdminBidLimits/models/bidLimitMatrix'
import type { CellKey } from '~/pages/AdminBidLimits/models/cellKey'

/** 3 segment 分の draft を管理する record 型 */
export type AllDrafts = Record<BidSegment, MatrixDraft>

/** 3 segment 分の saved snapshot (server 値) */
export type AllSaved = Record<BidSegment, MatrixDraft>

/** 3 segment 分の、 draft の元になった版の更新日時 */
export type AllBaseUpdatedAt = Record<BidSegment, Timestamp | undefined>

/** 3 segment 分の 閲覧 ⇄ 編集 の状態 (AC-BDL-006) */
export type AllEditing = Record<BidSegment, boolean>

type UseBidLimitMatrixDraftReturn = {
  drafts: AllDrafts
  saved: AllSaved
  /**
   * segment ごとの、 draft の元になった版の更新日時 (保存の楽観ロックの基準)。
   * 変更中の draft は再取得で保たれるので、 基準も一緒に保ち、 最新版の更新日時では送らない
   * (送ると他の利用者の更新を黙って上書きする)。
   */
  baseUpdatedAt: AllBaseUpdatedAt
  /**
   * segment ごとの編集モード (AC-BDL-006)。 draft と同じく page-level に置き、
   * タブ切替でカードが unmount されても保つ。 カード見出しの「編集中」
   * 表示もここを読む。
   */
  editing: AllEditing
  /** 編集モードに入る (「編集」ボタン) */
  startEditing: (segment: BidSegment) => void
  /** BFF response 受信時に draft + saved + 基準の更新日時を初期化する */
  initFromCells: (
    cells: BidLimitCell[],
    segmentMetas: BidLimitSegmentMeta[],
  ) => void
  /** 単一セルを更新する */
  setCell: (
    segment: BidSegment,
    key: CellKey,
    value: BidLimitValue | undefined,
  ) => void
  /** saved に戻し、編集モードを解除する (「変更を破棄」、AC-BDL-009) */
  resetDraft: (segment: BidSegment) => void
  /** 保存成功後に saved を draft と同じ値で更新し、編集モードを解除する */
  commitDraft: (segment: BidSegment, newDraft: MatrixDraft) => void
  /** 特定 segment が dirty か */
  isDirty: (segment: BidSegment) => boolean
  /** 3 segment のいずれかが dirty か (ページ遷移 guard 用) */
  isAnyDirty: boolean
}

const SEGMENTS = [
  BidSegment.SPOT_NORMAL,
  BidSegment.SPOT_BLOCK,
  BidSegment.INTRADAY,
]

function makeAllNotEditing(): AllEditing {
  return {
    [BidSegment.UNSPECIFIED]: false,
    [BidSegment.SPOT_NORMAL]: false,
    [BidSegment.SPOT_BLOCK]: false,
    [BidSegment.INTRADAY]: false,
  }
}

function makeEmptyAllBaseUpdatedAt(): AllBaseUpdatedAt {
  return {
    [BidSegment.UNSPECIFIED]: undefined,
    [BidSegment.SPOT_NORMAL]: undefined,
    [BidSegment.SPOT_BLOCK]: undefined,
    [BidSegment.INTRADAY]: undefined,
  }
}

function makeEmptyAllDrafts(): AllDrafts {
  return {
    [BidSegment.UNSPECIFIED]: emptyMatrixDraft(),
    [BidSegment.SPOT_NORMAL]: emptyMatrixDraft(),
    [BidSegment.SPOT_BLOCK]: emptyMatrixDraft(),
    [BidSegment.INTRADAY]: emptyMatrixDraft(),
  }
}

export function useBidLimitMatrixDraft(): UseBidLimitMatrixDraftReturn {
  const [drafts, setDrafts] = useState<AllDrafts>(makeEmptyAllDrafts)
  const [saved, setSaved] = useState<AllSaved>(makeEmptyAllDrafts)
  const [editing, setEditing] = useState<AllEditing>(makeAllNotEditing)
  const [baseUpdatedAt, setBaseUpdatedAt] = useState<AllBaseUpdatedAt>(
    makeEmptyAllBaseUpdatedAt,
  )
  // saved (server 値) の版。 破棄で draft を saved に戻す時、 基準もこの版に戻す。
  const savedUpdatedAt = useRef<AllBaseUpdatedAt>(makeEmptyAllBaseUpdatedAt())

  const startEditing = useCallback((segment: BidSegment) => {
    setEditing((prev) => ({ ...prev, [segment]: true }))
  }, [])

  // BFF response 受信時に saved snapshot を更新する。
  // dirty な segment の draft は保持する (= 別 segment の保存 / 即時 toggle
  // で本 segment の cache が再 fetch されても未保存編集が消えないように、
  // Devin BUG_pr-review-job-db736953f58848af99926b3ebabd6da0_0001 対応)。
  const initFromCells = useCallback(
    (cells: BidLimitCell[], segmentMetas: BidLimitSegmentMeta[]) => {
      const serverUpdatedAt = makeEmptyAllBaseUpdatedAt()
      for (const seg of SEGMENTS) {
        serverUpdatedAt[seg] = segmentMetas.find(
          (m) => m.bidSegment === seg,
        )?.updatedAt
      }
      savedUpdatedAt.current = serverUpdatedAt
      setSaved((prevSaved) => {
        setDrafts((prevDrafts) => {
          const nextDrafts: AllDrafts = { ...prevDrafts }
          const synced: BidSegment[] = []
          for (const seg of SEGMENTS) {
            const serverDraft = matrixDraftFromCells(cells, seg)
            // dirty な segment は draft をそのまま保持 (= 編集中の値を捨てない)。
            // dirty でなければ server 値で同期 (= 他 client からの更新を反映)。
            if (!isMatrixDirty(prevDrafts[seg], prevSaved[seg])) {
              nextDrafts[seg] = serverDraft
              synced.push(seg)
            }
          }
          setBaseUpdatedAt((prevBase) => {
            const nextBase: AllBaseUpdatedAt = { ...prevBase }
            for (const seg of synced) {
              nextBase[seg] = serverUpdatedAt[seg]
            }
            return nextBase
          })
          return nextDrafts
        })
        const nextSaved: AllSaved = { ...prevSaved }
        for (const seg of SEGMENTS) {
          nextSaved[seg] = matrixDraftFromCells(cells, seg)
        }
        return nextSaved
      })
    },
    [],
  )

  const setCell = useCallback(
    (segment: BidSegment, key: CellKey, value: BidLimitValue | undefined) => {
      setDrafts((prev) => {
        const prevDraft = prev[segment]
        const newCells = new Map(prevDraft.cells)
        if (value === undefined) {
          newCells.delete(key)
        } else {
          newCells.set(key, value)
        }
        return { ...prev, [segment]: { cells: newCells } }
      })
    },
    [],
  )

  const resetDraft = useCallback((segment: BidSegment) => {
    setSaved((prevSaved) => {
      setDrafts((prevDrafts) => ({
        ...prevDrafts,
        [segment]: { cells: new Map(prevSaved[segment].cells) },
      }))
      return prevSaved
    })
    setBaseUpdatedAt((prevBase) => ({
      ...prevBase,
      [segment]: savedUpdatedAt.current[segment],
    }))
    setEditing((prev) => ({ ...prev, [segment]: false }))
  }, [])

  const commitDraft = useCallback(
    (segment: BidSegment, newDraft: MatrixDraft) => {
      setSaved((prev) => ({
        ...prev,
        [segment]: { cells: new Map(newDraft.cells) },
      }))
      setEditing((prev) => ({ ...prev, [segment]: false }))
    },
    [],
  )

  const isDirty = useCallback(
    (segment: BidSegment) => isMatrixDirty(drafts[segment], saved[segment]),
    [drafts, saved],
  )

  const isAnyDirty = SEGMENTS.some((seg) =>
    isMatrixDirty(drafts[seg], saved[seg]),
  )

  return {
    drafts,
    saved,
    baseUpdatedAt,
    editing,
    startEditing,
    initFromCells,
    setCell,
    resetDraft,
    commitDraft,
    isDirty,
    isAnyDirty,
  }
}
